import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useFragrances } from '../../contexts/FragranceContext';
import { useAuth } from '../../contexts/AuthContext';
import { ozToMl, mlToOz } from '../../utils/sizeConversion';
import Button from '../ui/Button';
import { Fragrance } from '../fragrance/FragranceCard';

const ImportExport: React.FC = () => {
  const { collection, createFragrance, isLoading } = useFragrances();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const isDemoUser = user?.username === 'Demo';
  
  const [importStatus, setImportStatus] = useState<{
    isImporting: boolean;
    results?: { successful: number; failed: number; errors: any[] };
    error?: string;
  }>({ isImporting: false });

  // Export collection to CSV
  const exportToCSV = () => {
    if (!collection || collection.fragrances.length === 0) {
      alert('No fragrances to export');
      return;
    }

    const headers = [
      'House', 'Name', 'Concentration', 'Size (oz)', 'Size (ml)',
      'Top Notes', 'Middle Notes', 'Base Notes',
      'Is Clone', 'Original Fragrance',
      'Winter Rating', 'Spring Rating', 'Summer Rating', 'Fall Rating',
      'Longevity', 'Sillage', 'Day Rating', 'Night Rating', 'Overall Rating',
      'Personal Notes', 'Is Favorite', 'Is Decant'
    ];

    const csvContent = [
      headers.join(','),
      ...collection.fragrances.map(fragrance => [
        `"${fragrance.house}"`,
        `"${fragrance.name}"`,
        `"${fragrance.concentration || ''}"`,
        fragrance.size_oz || '',
        fragrance.size_ml || '',
        `"${fragrance.notes_top || ''}"`,
        `"${fragrance.notes_middle || ''}"`,
        `"${fragrance.notes_base || ''}"`,
        fragrance.is_clone ? 'TRUE' : 'FALSE',
        `"${fragrance.original_fragrance || ''}"`,
        fragrance.winter_rating || '',
        fragrance.spring_rating || '',
        fragrance.summer_rating || '',
        fragrance.fall_rating || '',
        fragrance.longevity || '',
        fragrance.sillage || '',
        fragrance.day_rating || '',
        fragrance.night_rating || '',
        fragrance.overall_rating || '',
        `"${fragrance.personal_notes || ''}"`,
        fragrance.is_favorite ? 'TRUE' : 'FALSE',
        fragrance.is_decant ? 'TRUE' : 'FALSE'
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `fragrance-collection-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Parse CSV file
  const parseCSV = (csvText: string): Partial<Fragrance>[] => {
    if (isDemoUser) return [];
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    // Helper to parse a CSV line respecting quotes
    const parseLine = (line: string): string[] => {
      const values: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            // Escaped quote
            current += '"';
            i++; // skip next quote
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      return values;
    };

    // Helper to normalize/split notes
    const splitNotes = (value: string): string => {
      return value
        .split(',')
        .map(n => n.trim())
        .filter(n => n.length > 0)
        .join(', ');
    };

    const headers = parseLine(lines[0]).map(h => h.replace(/"/g, '').trim());
    const fragrances: Partial<Fragrance>[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseLine(lines[i]).map(v => v.replace(/"/g, '').trim());
      const fragrance: Partial<Fragrance> = {};

      headers.forEach((header, index) => {
        const value = values[index];
        if (!value) return;

        switch (header.toLowerCase()) {
          case 'house':
            fragrance.house = value;
            break;
          case 'name':
            fragrance.name = value;
            break;
          case 'concentration':
            if (['EdC', 'EdT', 'EdP', 'Parfum', 'Elixir', 'Extrait'].includes(value)) {
              fragrance.concentration = value;
            }
            break;
          case 'size (oz)':
            const ozValue = parseFloat(value);
            if (!isNaN(ozValue)) {
              fragrance.size_oz = ozValue;
              // Auto-convert to ml using standard sizes
              fragrance.size_ml = ozToMl(ozValue);
            }
            break;
          case 'size (ml)':
            const mlValue = parseFloat(value);
            if (!isNaN(mlValue)) {
              fragrance.size_ml = mlValue;
              // Auto-convert to oz using standard sizes
              fragrance.size_oz = mlToOz(mlValue);
            }
            break;
          case 'top notes':
            fragrance.notes_top = splitNotes(value);
            break;
          case 'middle notes':
            fragrance.notes_middle = splitNotes(value);
            break;
          case 'base notes':
            fragrance.notes_base = splitNotes(value);
            break;
          case 'is clone':
            fragrance.is_clone = value.toLowerCase() === 'true';
            break;
          case 'original fragrance':
            fragrance.original_fragrance = value;
            break;
          case 'winter rating':
            const winterRating = parseFloat(value);
            if (!isNaN(winterRating) && winterRating >= 0 && winterRating <= 5) {
              // Round to nearest 0.25 for seasonal ratings
              fragrance.winter_rating = Math.round(winterRating * 4) / 4;
            }
            break;
          case 'spring rating':
            const springRating = parseFloat(value);
            if (!isNaN(springRating) && springRating >= 0 && springRating <= 5) {
              // Round to nearest 0.25 for seasonal ratings
              fragrance.spring_rating = Math.round(springRating * 4) / 4;
            }
            break;
          case 'summer rating':
            const summerRating = parseFloat(value);
            if (!isNaN(summerRating) && summerRating >= 0 && summerRating <= 5) {
              // Round to nearest 0.25 for seasonal ratings
              fragrance.summer_rating = Math.round(summerRating * 4) / 4;
            }
            break;
          case 'fall rating':
            const fallRating = parseFloat(value);
            if (!isNaN(fallRating) && fallRating >= 0 && fallRating <= 5) {
              // Round to nearest 0.25 for seasonal ratings
              fragrance.fall_rating = Math.round(fallRating * 4) / 4;
            }
            break;
          case 'longevity':
            const longevity = parseFloat(value);
            if (!isNaN(longevity) && longevity >= 1 && longevity <= 5) {
              // Round to whole numbers for longevity
              fragrance.longevity = Math.round(longevity);
            }
            break;
          case 'sillage':
            const sillage = parseFloat(value);
            if (!isNaN(sillage) && sillage >= 1 && sillage <= 4) {
              // Round to whole numbers for sillage
              fragrance.sillage = Math.round(sillage);
            }
            break;
          case 'day rating':
            const dayRating = parseFloat(value);
            if (!isNaN(dayRating) && dayRating >= 0 && dayRating <= 5) {
              // Round to nearest 0.25 for day/night ratings
              fragrance.day_rating = Math.round(dayRating * 4) / 4;
            }
            break;
          case 'night rating':
            const nightRating = parseFloat(value);
            if (!isNaN(nightRating) && nightRating >= 0 && nightRating <= 5) {
              // Round to nearest 0.25 for day/night ratings
              fragrance.night_rating = Math.round(nightRating * 4) / 4;
            }
            break;
          case 'overall rating':
            const overallRating = parseFloat(value);
            if (!isNaN(overallRating) && overallRating >= 1 && overallRating <= 10) {
              // Round to nearest 0.25 for overall rating
              fragrance.overall_rating = Math.round(overallRating * 4) / 4;
            }
            break;
          case 'personal notes':
            fragrance.personal_notes = value;
            break;
          case 'is favorite':
            fragrance.is_favorite = value.toLowerCase() === 'true';
            break;
          case 'is decant':
            fragrance.is_decant = value.toLowerCase() === 'true';
            break;
        }
      });

      // Only add if we have at least house and name
      if (fragrance.house && fragrance.name) {
        fragrances.push(fragrance);
      }
    }

    return fragrances;
  };


  // Handle file import
  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isDemoUser) return;
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        setImportStatus({ isImporting: true });
        
        const csvText = e.target?.result as string;
        const fragrances = parseCSV(csvText);

        if (fragrances.length === 0) {
          setImportStatus({
            isImporting: false,
            error: 'No valid fragrances found in CSV file'
          });
          return;
        }

        // Import fragrances one by one
        let successful = 0;
        let failed = 0;
        const errors: any[] = [];

        for (const fragrance of fragrances) {
          try {
            const result = await createFragrance(fragrance);
            if (result.success) {
              successful++;
            } else {
              failed++;
              errors.push({ fragrance, error: result.message });
            }
          } catch (error) {
            failed++;
            errors.push({ fragrance, error: 'Unknown error' });
          }
        }

        setImportStatus({
          isImporting: false,
          results: { successful, failed, errors }
        });

      } catch (error) {
        setImportStatus({
          isImporting: false,
          error: 'Failed to parse CSV file'
        });
      }
    };

    reader.readAsText(file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileInput = () => {
    if (isDemoUser) return;
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Import & Export
        </h2>
      </div>

      {/* Export Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Export Collection
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Download your entire fragrance collection as a CSV file for backup or sharing.
        </p>
        <Button
          onClick={exportToCSV}
          disabled={!collection || collection.fragrances.length === 0}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export to CSV
        </Button>
      </motion.div>

      {/* Import Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Import Collection
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Import fragrances from a CSV file. The file should include columns for House, Name, and optionally other fields.
        </p>
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileImport}
          disabled={isDemoUser}
          className="hidden"
        />
        
        <Button
          onClick={triggerFileInput}
          disabled={importStatus.isImporting || isDemoUser}
          isLoading={importStatus.isImporting}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l3-3m0 0l3 3m-3-3v10" />
          </svg>
          {isDemoUser ? 'Unavailable in Demo' : importStatus.isImporting ? 'Importing... Please Be Patient' : 'Import from CSV'}
        </Button>

        {/* Import Results */}
        {importStatus.results && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
          >
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              Import Results
            </h4>
            <div className="text-sm space-y-1">
              <p className="text-green-600 dark:text-green-400">
                ✅ Successfully imported: {importStatus.results.successful} fragrances
              </p>
              {importStatus.results.failed > 0 && (
                <p className="text-red-600 dark:text-red-400">
                  ❌ Failed to import: {importStatus.results.failed} fragrances
                </p>
              )}
            </div>
            
            {importStatus.results.errors.length > 0 && (
              <details className="mt-3">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
                  View Error Details
                </summary>
                <div className="mt-2 space-y-2 text-xs">
                  {importStatus.results.errors.slice(0, 5).map((error, index) => (
                    <div key={index} className="p-2 bg-red-50 dark:bg-red-900/20 rounded">
                      <p className="text-red-600 dark:text-red-400">
                        Row {error.row}: {error.error}
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">
                        Data: {JSON.stringify(error.data, null, 2)}
                      </p>
                    </div>
                  ))}
                  {importStatus.results.errors.length > 5 && (
                    <p className="text-gray-600 dark:text-gray-400">
                      ... and {importStatus.results.errors.length - 5} more errors
                    </p>
                  )}
                </div>
              </details>
            )}
          </motion.div>
        )}

        {importStatus.error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
          >
            <p className="text-red-600 dark:text-red-400 text-sm">
              {importStatus.error}
            </p>
          </motion.div>
        )}
      </motion.div>

      {/* CSV Format Guide */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          CSV Format Guide
        </h3>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          <p><strong>Required columns:</strong> House, Name</p>
          <p><strong>Optional columns:</strong> Concentration, Size (oz), Size (ml), Top Notes, Middle Notes, Base Notes, Winter Rating, Spring Rating, Summer Rating, Fall Rating, Longevity, Sillage, Day Rating, Night Rating, Overall Rating, Personal Notes, Is Clone, Original Fragrance, Is Favorite, Is Decant</p>
          <p><strong>Rating ranges:</strong></p>
          <ul className="ml-4 space-y-1">
            <li>• Seasonal ratings: 0-5</li>
            <li>• Longevity: 1-5 (1=Very Weak, 2=Weak, 3=Moderate, 4=Long Lasting, 5=Eternal)</li>
            <li>• Sillage: 1-4 (1=Intimate, 2=Moderate, 3=Strong, 4=Enormous)</li>
            <li>• Day/Night ratings: 0-5</li>
            <li>• Overall rating: 1-10</li>
          </ul>
          <p><strong>Boolean values:</strong> Use TRUE/FALSE for Is Clone, Is Favorite, Is Decant</p>
          <p><strong>Concentration values:</strong> EdC, EdT, EdP, Parfum, Elixir, Extrait</p>
        </div>
      </motion.div>
    </div>
  );
};

export default ImportExport;
